/**
 * Chats history panel.
 *
 * Lives at the bottom of the left column under the documents tree.
 * Lists every chat session the operator has opened in this tenant.
 * New chats are created implicitly: hitting Run on a variation
 * spawns a session linked to the new assessment, and typing a
 * message into the empty AssistantPanel auto-creates one. The
 * panel itself has no "new" entry-point - the explicit
 * "New variation" button used to live here but duplicated the
 * Run flow's session creation and confused which path was
 * authoritative; selecting a session promotes it into the middle
 * Assistant panel without leaving the workspace.
 */

import { useEffect, useMemo, useState } from "react";
import {
  listChatSessions,
  loadConfig,
  type ApiError,
  type ChatSessionSummary,
} from "../api/client";

export function ChatHistoryPanel({
  currentSessionId,
  onSelect,
  refreshKey,
}: {
  currentSessionId: string | null;
  onSelect: (sessionId: string) => void;
  refreshKey: number;
}) {
  const [sessions, setSessions] = useState<ChatSessionSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const cfg = loadConfig();
    setLoading(true);
    listChatSessions(cfg)
      .then((list) => {
        if (!cancelled) {
          setSessions(list);
          setError(null);
        }
      })
      .catch((err: ApiError) => {
        if (!cancelled) {
          setError(err.message ?? "Could not load chat history");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const sorted = useMemo(
    () =>
      sessions
        // Show only Workspace (per-variation) chats; Tab-2 contractor
        // chats live on their own page. Filtering on the explicit
        // kind (not on whether contract_id is absent) is what lets a
        // Workspace chat carry a contract_id for retrieval scoping
        // without disappearing from this list. See ADR-0015.
        .filter((s) => s.kind === "workspace")
        .sort(
          (a, b) =>
            new Date(b.updated_at).getTime() -
            new Date(a.updated_at).getTime(),
        ),
    [sessions],
  );

  return (
    <div className="chats-panel" aria-label="Chat history">
      <div className="col-head chats-head">
        <div className="col-title">CHATS</div>
      </div>
      {loading && sorted.length === 0 && (
        <div className="empty empty-compact">
          <p>Loading chats…</p>
        </div>
      )}
      {error && (
        <div className="empty empty-compact" style={{ color: "var(--err)" }}>
          <p>{error}</p>
        </div>
      )}
      {!loading && !error && sorted.length === 0 && (
        <div className="empty empty-compact">
          <p>
            No chats yet. Hit <strong>Run</strong> on a variation, or
            type a message in the assistant to start one.
          </p>
        </div>
      )}
      <div className="chats-list">
        {sorted.map((s) => (
          <button
            key={s.session_id}
            type="button"
            className="chat-row"
            aria-current={s.session_id === currentSessionId || undefined}
            onClick={() => onSelect(s.session_id)}
          >
            <span className="chat-row-title">{s.title}</span>
            <span className="chat-row-meta">
              {s.variation_label && <span className="chat-row-label">{s.variation_label}</span>}
              <span className="chat-row-count">
                {s.message_count} {s.message_count === 1 ? "msg" : "msgs"}
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
